import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { FC, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  type Counterparty,
  type CounterpartyListCounterpartiesData,
  type CounterpartyListCounterpartiesResponse,
  counterpartyGetCounterparty,
  counterpartyListCounterparties,
  counterpartyListCounterpartiesQueryKey,
} from '@column/api';
import { Button, Currency, Dropdown, DropdownOption, Icon } from '@column/column-ui-kit';

import { ModalCounterparty, ModalInternationalCounterparty } from '~/components/Dialog';
import { useDebounce } from '~/hooks/useDebounce';
import {
  type HighlightSuffixCandidate,
  getHighlightedField,
  getHighlightedSuffix,
  renderHighlight,
} from '~/lib/search/highlight';
import { useSessionStore } from '~/stores/Session';

export type CounterpartyModalType = 'ach' | 'wire' | 'intl-wire' | 'realtime';

type CounterpartyFilters = NonNullable<CounterpartyListCounterpartiesData['query']>;

export const counterpartyDropdownListBaseQueryKey = ['counterpartyDropdown', 'list'] as const;
const counterpartyDropdownListQueryKey = (platformId?: string, isSandbox?: boolean, query?: CounterpartyFilters) =>
  [...counterpartyDropdownListBaseQueryKey, platformId, isSandbox, query] as const;

// --- Label components ---

const SUFFIX_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'BENEFICIARY_EMAIL', label: 'Email' },
  { field: 'BENEFICIARY_PHONE', label: 'Phone' },
  { field: 'LOCAL_ACCOUNT_NUMBER', label: 'Local Acc#' },
  { field: 'LOCAL_BANK_CODE', label: 'Local Bank' },
  { field: 'COUNTERPARTY_ID', label: 'Counterparty ID' },
];

const HighlightedLabel: FC<{ counterparty: Counterparty }> = ({ counterparty }) => {
  const highlightedName = getHighlightedField(counterparty.search_metadata, 'BENEFICIARY_NAME');
  const highlightedDescription = getHighlightedField(counterparty.search_metadata, 'DESCRIPTION');
  const namePart = highlightedName
    ? renderHighlight(highlightedName)
    : counterparty.name || counterparty.wire?.beneficiary_name;
  const descriptionPart = highlightedDescription ? renderHighlight(highlightedDescription) : counterparty.description;

  if (namePart && descriptionPart) {
    return (
      <>
        {namePart} ({descriptionPart})
      </>
    );
  }
  return <>{namePart || descriptionPart || 'Unnamed'}</>;
};

const HighlightedSmall: FC<{ counterparty: Counterparty }> = ({ counterparty }) => {
  const highlightedRouting = getHighlightedField(counterparty.search_metadata, 'ROUTING_NUMBER');
  const highlightedAccount = getHighlightedField(counterparty.search_metadata, 'ACCOUNT_NUMBER');
  const suffix = getHighlightedSuffix(counterparty.search_metadata, SUFFIX_CANDIDATES);
  return (
    <>
      Rtn# {highlightedRouting ? renderHighlight(highlightedRouting) : counterparty.routing_number} / Acc#{' '}
      {highlightedAccount ? renderHighlight(highlightedAccount) : counterparty.account_number}
      {suffix ? (
        <>
          {' · '}
          {suffix.label}: {renderHighlight(suffix.fragment)}
        </>
      ) : null}
    </>
  );
};

export const formatCounterpartyLabel = (cp: Counterparty) =>
  cp.name && cp.description
    ? `${cp.name} (${cp.description})`
    : cp.description || cp.name || cp.wire?.beneficiary_name || 'Unnamed';

const PlainLabel: FC<{ counterparty: Counterparty }> = ({ counterparty }) => (
  <>{formatCounterpartyLabel(counterparty)}</>
);

const CounterpartyOptionLabel: FC<{
  counterparty: Counterparty;
  highlightSearch: boolean;
  hasSearchQuery: boolean;
}> = ({ counterparty, highlightSearch, hasSearchQuery }) => {
  if (hasSearchQuery && highlightSearch && counterparty.search_metadata?.highlights) {
    return <HighlightedLabel counterparty={counterparty} />;
  }
  return <PlainLabel counterparty={counterparty} />;
};

// --- Shared hook ---

interface UseCounterpartyOptionsConfig {
  value?: string | null;
}

export const useCounterpartyOptions = (config: UseCounterpartyOptionsConfig = {}) => {
  const { value } = config;
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const [searchText, setSearchText] = useState<string | undefined>();

  const effectiveQuery = useMemo(() => {
    if (searchText) return { query: searchText, deduplicate: true };
    return undefined;
  }, [searchText]);

  const counterpartiesQuery = useInfiniteQuery({
    queryKey: counterpartyDropdownListQueryKey(platformId, isSandbox, effectiveQuery),
    queryFn: async ({ pageParam, signal }) => {
      const startingAfter = typeof pageParam === 'string' ? pageParam : undefined;
      const { data } = await counterpartyListCounterparties({
        query: { ...effectiveQuery, starting_after: startingAfter },
        signal,
        throwOnError: true,
      });
      return data as CounterpartyListCounterpartiesResponse;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.has_more) return undefined;
      return lastPage.counterparties?.[lastPage.counterparties.length - 1]?.id;
    },
    initialPageParam: undefined as string | undefined,
    placeholderData: keepPreviousData,
  });

  const resolvedId = value ?? undefined;

  const counterparties = useMemo(() => {
    const entries = counterpartiesQuery.data?.pages.flatMap((page) => page?.counterparties ?? []) ?? [];
    return [...new Map(entries.map((entry) => [entry.id, entry])).values()];
  }, [counterpartiesQuery.data]);

  const activeCounterpartyQuery = useQuery({
    queryKey: ['counterpartyDropdown', 'get', platformId, isSandbox, resolvedId],
    queryFn: async () => {
      const { data } = await counterpartyGetCounterparty({
        path: { counterparty_id: resolvedId! },
        throwOnError: true,
      });
      return data;
    },
    enabled: Boolean(resolvedId),
    placeholderData: () => counterparties.find((cp) => cp.id === resolvedId),
    staleTime: 5 * 60_000, // 5 minutes
    // This GET only enriches the list data we already have; a 403 (per-account
    // authorization) is deterministic, so don't retry it into a console storm.
    retry: false,
  });

  const activeCounterparty = activeCounterpartyQuery.data as Counterparty | undefined;

  const hasSearchQuery = Boolean(searchText);

  const [search] = useDebounce((query?: string) => setSearchText(query || undefined), 300, []);

  const toOption = useCallback(
    (cp: Counterparty, highlightSearch: boolean): DropdownOption => ({
      label: (
        <CounterpartyOptionLabel counterparty={cp} highlightSearch={highlightSearch} hasSearchQuery={hasSearchQuery} />
      ),
      small:
        hasSearchQuery && highlightSearch && cp.search_metadata?.highlights ? (
          <HighlightedSmall counterparty={cp} />
        ) : (
          `Rtn# ${cp.routing_number} / Acc# ${cp.account_number}`
        ),
      value: cp.id,
    }),
    [hasSearchQuery]
  );

  const options: DropdownOption[] = useMemo(
    () => counterparties.map((cp) => toOption(cp, true)),
    [counterparties, toOption]
  );

  const hiddenOptions = useMemo(() => {
    if (!resolvedId) return [];

    const selected =
      counterparties.find((cp) => cp.id === resolvedId) ||
      (activeCounterparty?.id === resolvedId ? activeCounterparty : undefined);

    return selected ? [toOption(selected, false)] : [];
  }, [activeCounterparty, resolvedId, counterparties, toOption]);

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData } = counterpartiesQuery;
  const handleScrollEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isPlaceholderData) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData]);

  return {
    options,
    hiddenOptions,
    search,
    handleScrollEnd,
    hasSearchQuery,
    counterparties,
    activeCounterparty,
    activeCounterpartyIsLoading: activeCounterpartyQuery.isLoading,
  };
};

// --- Styled ---

const CounterpartyFieldRow = styled.div`
  display: flex;
  gap: 8px;
`;

// --- Component ---

export interface CounterpartyDropdownProps {
  value?: string | null;
  onChange: (value: string) => void;
  type?: CounterpartyModalType;
  onCounterpartyCreated?: (counterparty: Counterparty, defaultCurrency?: Currency) => void;
  editValues?: Counterparty;
  onReset?: () => void;
  showEditReset?: boolean;
  fullWidth?: boolean;
  maxWidth?: string;
  hasError?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  searchLabel?: string;
  preventSelect?: boolean;
}

export const CounterpartyDropdown: FC<CounterpartyDropdownProps> = ({
  value,
  onChange,
  type,
  onCounterpartyCreated,
  editValues,
  onReset,
  showEditReset,
  fullWidth,
  maxWidth,
  hasError,
  placeholder,
  isDisabled,
  searchLabel,
  preventSelect,
}) => {
  const { options, hiddenOptions, search, handleScrollEnd } = useCounterpartyOptions({ value });
  const queryClient = useQueryClient();
  const [isCounterpartyModalOpen, setIsCounterpartyModalOpen] = useState(false);
  const [isIntlCounterpartyModalOpen, setIsIntlCounterpartyModalOpen] = useState(false);
  const [intlModalValues, setIntlModalValues] = useState<Counterparty | undefined>(undefined);

  const handleCounterpartyCreated = useCallback(
    (counterparty: Counterparty, defaultCurrency?: Currency) => {
      setIsCounterpartyModalOpen(false);
      setIsIntlCounterpartyModalOpen(false);
      queryClient.invalidateQueries({ queryKey: counterpartyListCounterpartiesQueryKey() });
      queryClient.invalidateQueries({ queryKey: counterpartyDropdownListBaseQueryKey });
      onCounterpartyCreated?.(counterparty, defaultCurrency);
    },
    [onCounterpartyCreated, queryClient]
  );

  const handleNewClick = useCallback(() => {
    if (type === 'intl-wire') {
      setIntlModalValues(undefined);
      setIsIntlCounterpartyModalOpen(true);
    } else {
      setIsCounterpartyModalOpen(true);
    }
  }, [type]);

  const handleEditClick = useCallback(() => {
    setIntlModalValues(editValues);
    setIsIntlCounterpartyModalOpen(true);
  }, [editValues]);

  const showModals = Boolean(type);

  return (
    <>
      <CounterpartyFieldRow>
        <Dropdown
          options={options}
          hiddenOptions={hiddenOptions}
          active={value ?? undefined}
          fullWidth={fullWidth}
          maxWidth={maxWidth}
          emptyMessage="No counterparties found"
          onSearchChange={(query: string) => search(query)}
          onChange={onChange}
          onScrollEnd={handleScrollEnd}
          hasError={hasError}
          isDisabled={isDisabled}
          placeholder={placeholder}
          searchLabel={searchLabel}
          preventSelect={preventSelect}
          search
        />
        {showEditReset ? (
          <CounterpartyFieldRow>
            <Button variant="secondary" onClick={handleEditClick} type="button">
              Edit
            </Button>
            <Button variant="secondary" onClick={onReset} type="button">
              Reset
            </Button>
          </CounterpartyFieldRow>
        ) : showModals ? (
          <Button variant="secondary" icon={<Icon.Plus />} onClick={handleNewClick} type="button">
            New
          </Button>
        ) : null}
      </CounterpartyFieldRow>

      {showModals && (
        <>
          <ModalCounterparty
            isOpen={isCounterpartyModalOpen}
            onClose={() => setIsCounterpartyModalOpen(false)}
            callback={handleCounterpartyCreated}
            type={type}
          />
          <ModalInternationalCounterparty
            isOpen={isIntlCounterpartyModalOpen}
            onClose={() => setIsIntlCounterpartyModalOpen(false)}
            values={intlModalValues as never}
            callback={handleCounterpartyCreated as never}
          />
        </>
      )}
    </>
  );
};
